import type { FC, ReactNode } from 'react'
import type { SampleKind } from '@/constants/materials'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/components/generals/count-up'
import { TweenNumber } from '@/components/generals/tween-number'
import { MaterialSample } from '@/components/materials/material-sample'

import { MATERIALS, MAX_CARBON } from '@/constants/materials'

interface PickerProps {
    label: string
    value: SampleKind
    other: SampleKind
    onChange: (value: SampleKind) => void
}

const Picker: FC<PickerProps> = ({ label, value, other, onChange }): ReactNode => {
    return (
        <div role='group' aria-label={label}>
            <p className='hud mb-3 text-mute'>
                {label}
            </p>

            <div className='flex flex-wrap gap-2'>
                {MATERIALS.map(material => (
                    <button
                        key={material.id}
                        type='button'
                        aria-pressed={value === material.id}
                        disabled={other === material.id}
                        onClick={() => onChange(material.id)}
                        className={cn(
                            'hud h-9 border px-3 transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-35',
                            value === material.id ? 'border-ink bg-ink text-background' : 'border-hairline text-mute hover:border-mute hover:text-ink'
                        )}
                    >
                        {material.name}
                    </button>
                ))}
            </div>
        </div>
    )
}

/**
 * Two materials side by side on the same scale, with the ratio between them.
 */
export const MaterialCompare: FC = (): ReactNode => {
    const [left, setLeft] = useState<SampleKind>('timber')
    const [right, setRight] = useState<SampleKind>('steel')
    const pair = [left, right].map(id => MATERIALS.find(material => material.id === id) ?? MATERIALS[0])
    const [a, b] = pair
    const high = a.carbon >= b.carbon ? a : b
    const low = high === a ? b : a
    const ratio = high.carbon / Math.max(1, low.carbon)

    return (
        <div>
            <div className='grid gap-6 border-b border-hairline px-gutter pb-8 sm:grid-cols-2'>
                <Picker label='Material A' value={left} other={right} onChange={setLeft} />
                <Picker label='Material B' value={right} other={left} onChange={setRight} />
            </div>

            <div className='grid gap-px bg-hairline sm:grid-cols-2'>
                {pair.map((material, index) => (
                    <article key={`${index}-${material.id}`} className='grid gap-6 bg-background px-gutter py-10 desk:grid-cols-[minmax(0,1fr)_200px]'>
                        <div>
                            <p className='hud text-mute'>
                                {index === 0 ? 'A' : 'B'} / {material.detail}
                            </p>

                            <h3 className='mt-3 font-heading text-[clamp(28px,3vw,42px)] font-medium uppercase leading-none tracking-[-0.02em] text-ink'>
                                {material.name}
                            </h3>

                            <p className='mt-8 font-heading text-[clamp(48px,5vw,72px)] font-medium leading-none tracking-[-0.04em] text-ink'>
                                <TweenNumber value={material.carbon} duration={700} />
                            </p>

                            <p className='hud mt-2 text-mute'>
                                kgCO2e per m³
                            </p>

                            <div className='mt-5 h-2 w-full bg-hairline/60' aria-hidden='true'>
                                <span className='race-bar block h-full bg-ink' style={{ width: `${Math.max(0.6, (material.carbon / MAX_CARBON) * 100)}%` }} />
                            </div>

                            <p className='hud mt-5 text-ink'>
                                {material.share}% of portfolio mass
                            </p>
                        </div>

                        <div className='relative aspect-square w-full max-w-[200px] border border-hairline'>
                            <MaterialSample kind={material.id} label={material.sampleLabel} className='absolute inset-3 h-[calc(100%-1.5rem)] w-[calc(100%-1.5rem)]' />
                        </div>
                    </article>
                ))}
            </div>

            <p className='hud border-t border-hairline px-gutter py-5 text-ink' aria-live='polite'>
                {high.name} holds {formatNumber(ratio, ratio < 10 ? 1 : 0)} times the embodied carbon of {low.name.toLowerCase()} per m³
            </p>
        </div>
    )
}
